const DEFAULT_METRIC_OVERLAY_BOX_SELECTOR = '.dbi-metric-select-overlay .tea-dropdown-box';

const DEFAULT_SYSTEM_HEADER_HEIGHT = 56;
const MIN_HEADER_HEIGHT = 30;
const MAX_HEADER_HEIGHT = 120;

const SYSTEM_HEADER_SELECTORS = [
  '.dbi-header',
  '.db-header',
  '.db-layout-header',
  '.tea-layout__header',
  '.layout-header',
  '.header',
];

let cachedSystemHeaderHeight: number | null = null;

const isSystemHeaderRect = (rect: DOMRect) => {
  return (
    rect.top <= 0 &&
    rect.bottom > MIN_HEADER_HEIGHT &&
    rect.height <= MAX_HEADER_HEIGHT &&
    rect.width > window.innerWidth * 0.5
  );
};

const getSystemHeaderHeightBySelector = () => {
  const header = SYSTEM_HEADER_SELECTORS.map((selector) => document.querySelector(selector)).find(
    (node): node is HTMLElement => {
      if (!(node instanceof HTMLElement)) {
        return false;
      }

      return isSystemHeaderRect(node.getBoundingClientRect());
    },
  );

  const headerRect = header?.getBoundingClientRect();
  if (!headerRect?.height) {
    return 0;
  }

  return headerRect.bottom > 0 ? headerRect.bottom : headerRect.height;
};

const getSystemHeaderHeightByLayout = () => {
  const nodes = Array.from(document.body.querySelectorAll<HTMLElement>('*'));
  const headerBottomList = nodes
    .map((node) => {
      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);
      const isTopFixed = ['fixed', 'sticky'].includes(style.position) && isSystemHeaderRect(rect);

      return isTopFixed ? rect.bottom : 0;
    })
    .filter(Boolean);

  return Math.max(0, ...headerBottomList);
};

const getSystemHeaderHeight = () => {
  if (cachedSystemHeaderHeight !== null) {
    return cachedSystemHeaderHeight;
  }

  const headerHeight = getSystemHeaderHeightBySelector() || getSystemHeaderHeightByLayout();

  if (headerHeight) {
    cachedSystemHeaderHeight = Math.ceil(headerHeight);
    return cachedSystemHeaderHeight;
  }

  cachedSystemHeaderHeight = DEFAULT_SYSTEM_HEADER_HEIGHT;
  return cachedSystemHeaderHeight;
};

const getMetricOverlayBox = (overlayBoxSelector: string) => {
  const overlayBox = document.querySelector(overlayBoxSelector);
  if (overlayBox instanceof HTMLElement) {
    return overlayBox;
  }

  return null;
};

const limitOverlayTop = (overlayBoxSelector: string) => {
  const overlayBox = getMetricOverlayBox(overlayBoxSelector);
  if (!overlayBox) {
    return;
  }

  const headerHeight = getSystemHeaderHeight();
  const overlayRect = overlayBox.getBoundingClientRect();
  const currentMarginTop = Number.parseFloat(overlayBox.style.marginTop || '0') || 0;
  const originalTop = overlayRect.top - currentMarginTop;
  const offsetTop = Math.max(headerHeight - originalTop, 0);
  const nextMarginTop = offsetTop > 0 ? `${offsetTop}px` : '';

  if (overlayBox.style.marginTop !== nextMarginTop) {
    overlayBox.style.marginTop = nextMarginTop;
  }
};

export const startLimitMetricOverlayTop = (overlayBoxSelector = DEFAULT_METRIC_OVERLAY_BOX_SELECTOR) => {
  let frameId = 0;
  let observer: MutationObserver | null = null;
  const timerIds: number[] = [];

  const scheduleLimitOverlayTop = () => {
    window.cancelAnimationFrame(frameId);
    limitOverlayTop(overlayBoxSelector);
    frameId = window.requestAnimationFrame(() => {
      limitOverlayTop(overlayBoxSelector);
    });
  };

  const handleResize = () => {
    cachedSystemHeaderHeight = null;
    scheduleLimitOverlayTop();
  };

  const observeOverlayPositionChange = () => {
    const overlayBox = getMetricOverlayBox(overlayBoxSelector);
    if (!overlayBox) {
      return;
    }

    observer?.disconnect();
    observer = new MutationObserver(() => {
      limitOverlayTop(overlayBoxSelector);
    });

    let overlayWrapper = overlayBox.parentElement;
    let observeLevel = 0;
    while (overlayWrapper && overlayWrapper !== document.body && observeLevel < 4) {
      observer.observe(overlayWrapper, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      overlayWrapper = overlayWrapper.parentElement;
      observeLevel += 1;
    }

    observer.observe(overlayBox, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  };

  scheduleLimitOverlayTop();
  [0, 50, 150, 300].forEach((delay) => {
    const timerId = window.setTimeout(() => {
      scheduleLimitOverlayTop();
      observeOverlayPositionChange();
    }, delay);
    timerIds.push(timerId);
  });
  window.addEventListener('scroll', scheduleLimitOverlayTop, true);
  window.addEventListener('resize', handleResize);

  return () => {
    timerIds.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    window.cancelAnimationFrame(frameId);
    observer?.disconnect();
    window.removeEventListener('scroll', scheduleLimitOverlayTop, true);
    window.removeEventListener('resize', handleResize);

    const overlayBox = getMetricOverlayBox(overlayBoxSelector);
    if (overlayBox) {
      overlayBox.style.marginTop = '';
    }
  };
};
